// bootstrap, ผู้ใช้, ตัวละคร, จักรวาล, persona/scenario, โซเชียล, อัปโหลด, อันดับ — พอร์ตจาก Content.gs
import {
  fb, fk, err, str, num, bool, nowIso, newId, dayKey, limitOf, parseJson, asArray, pick, getRow, rowsOf, putRow, putNew,
  updateRow, incr, bump, readAll, settings, setting, PUBLIC_SETTINGS, pageByKey, pageRows, rateLimit, memoize,
} from "./core";
import { getUser, publicSelf, publicUser } from "./auth";
import { notify, changeCredits, vipState, vipPublic, packagesList, modelsList, giftsList, currentStreak, checkinReward } from "./economy";
import { looksExplicit, LANG_NAME } from "./ai";
import { isPersonaId, resolvePersona } from "../ai/persona";
import { cardOf, charCards, dropCards, getCharacter, getPrompt, canSeeCharacter, trendScore, newShareCode } from "./chars";

// ---- bootstrap / me ---------------------------------------------------------------------
export async function bootstrap(p, ctx) {
  const [u, st, pk, models, gifts, tags, notifs, done] = await Promise.all([
    getUser(ctx.uid), settings(), packagesList(), modelsList(), giftsList(), tagsList(),
    pageByKey(`notifications/${fk(ctx.uid)}`, { limit: 50 }), fb.get(`checkins/${fk(ctx.uid)}/${dayKey()}`),
  ]);
  const v = await vipState(u);
  const pub = {};
  PUBLIC_SETTINGS.forEach((k) => {
    pub[k] = st[k];
  });
  const streak = currentStreak(u);
  return {
    me: publicSelf(u), vip: vipPublic(v), settings: pub, packages: pk.items, vipTiers: v.tiers, models: models.items,
    gifts: gifts.items, tags: tags.items, unread: notifs.items.filter((n) => n.isRead !== true).length,
    checkin: { canClaim: !done, streak, todayCoins: (await checkinReward(u, done ? streak : streak + 1)).base },
    isSuper: ctx.isSuper,
  };
}

export async function meGet(p, ctx) {
  const u = await getUser(ctx.uid);
  return { user: publicSelf(u), vip: vipPublic(await vipState(u)), isSuper: ctx.isSuper };
}

export async function meUpdate(p, ctx) {
  const patch = {};
  if (p.displayName !== undefined) patch.displayName = str(p.displayName, 20, "ชื่อ", true);
  if (p.bio !== undefined) patch.bio = str(p.bio, 500);
  if (p.photoURL !== undefined) patch.photoURL = str(p.photoURL, 500);
  if (p.backgroundImage !== undefined) patch.backgroundImage = str(p.backgroundImage, 500);
  if (p.socialLinks !== undefined) patch.socialLinks = asArray(p.socialLinks, 3).map((x) => String(x).slice(0, 200));
  if (p.preferences !== undefined) {
    const pr = parseJson(p.preferences, {});
    if (!pr || typeof pr !== "object" || JSON.stringify(pr).length > 4000) throw err("bad_request", "preferences ไม่ถูกต้อง");
    patch.preferences = pr;
  }
  if (p.username !== undefined) {
    const un = str(p.username, 20).toLowerCase();
    const cur = await getUser(ctx.uid);
    if (un) {
      if (!/^[a-z0-9_.]{3,20}$/.test(un)) throw err("bad_request", "ชื่อผู้ใช้ใช้ได้เฉพาะ a-z 0-9 _ . (3-20 ตัว)");
      if (un !== cur.username) {
        const key = `usernames/${fk(un)}`;
        if (!(await fb.createOnly(key, ctx.uid)) && (await fb.get(key)) !== ctx.uid) throw err("bad_request", "ชื่อผู้ใช้นี้ถูกใช้แล้ว");
      }
    }
    if (cur.username && cur.username !== un) await fb.remove(`usernames/${fk(cur.username)}`).catch(() => {});
    patch.username = un || null;
  }
  const u = await updateRow(`users/${fk(ctx.uid)}`, patch);
  return { user: publicSelf({ ...u, preferences: u.preferences || {}, socialLinks: u.socialLinks || [] }) };
}

export async function profileGet(p, ctx) {
  let u = null;
  if (p.userId) u = await getUser(str(p.userId, 100));
  else if (p.username) {
    const id = await fb.get(`usernames/${fk(str(p.username, 20).toLowerCase())}`);
    if (id) u = await getUser(id);
  }
  if (!u) throw err("not_found", "ไม่พบผู้ใช้");
  const following = !!((await fb.get(`follows/${fk(ctx.uid)}/${fk(u.id)}`)) || {}).active;
  const chars = (await charCards()).filter((c) => c.createdBy === u.id).slice(0, 50);
  return { user: publicUser(u), isFollowing: following, characters: chars };
}

// ---- แท็ก ------------------------------------------------------------------------------------
export async function tagsList() {
  return { items: (await readAll("character_tags")).filter((t) => t.isActive !== false).sort((a, b) => num(a.sort) - num(b.sort)).map((t) => pick(t, ["id", "name", "category", "sort"])) };
}

// ---- ตัวละคร ---------------------------------------------------------------------------------
export async function charactersList(p, ctx) {
  let items = (await charCards()).slice();
  const tags = asArray(p.tags);
  if (tags.length) items = items.filter((c) => tags.every((t) => (c.tagIds || []).indexOf(t) !== -1));
  if (p.rating) items = items.filter((c) => c.contentRating === p.rating);
  if (p.flag) items = items.filter((c) => c.flag === p.flag);
  if (p.creator) items = items.filter((c) => c.createdBy === p.creator);
  if (p.q) {
    const q = String(p.q).toLowerCase();
    items = items.filter((c) => (c.name + " " + (c.tagline || "")).toLowerCase().indexOf(q) !== -1);
  }
  let sort = p.sort || "new";
  if (sort === "following") {
    const set = {};
    rowsOf(await fb.get(`follows/${fk(ctx.uid)}`)).forEach((f) => {
      if (f.active === true) set[f.followingId] = 1;
    });
    items = items.filter((c) => set[c.createdBy]);
    sort = "new";
  }
  if (sort === "popular") items.sort((a, b) => num(b.messageCount) - num(a.messageCount));
  else if (sort === "trending") items.sort((a, b) => trendScore(b) - trendScore(a));
  else items.sort((a, b) => (String(b.createdAt) < String(a.createdAt) ? -1 : 1));
  const limit = limitOf(p.limit, 24), page = Math.max(1, parseInt(p.page, 10) || 1), off = (page - 1) * limit;
  return { items: items.slice(off, off + limit), page, limit, hasMore: off + limit < items.length, total: items.length };
}

/** เจ้าของ/super เห็น description (prompt) ; คนอื่นไม่เห็น */
async function characterFull(c, uid, isSuper) {
  const o = { ...c };
  const owner = c.createdBy === uid;
  if (owner || isSuper) o.description = await getPrompt(c.id);
  o.isOwner = owner;
  return o;
}

export async function charactersGet(p, ctx) {
  let c = await getCharacter(str(p.id, 80, "id", true));
  if (!canSeeCharacter(c, ctx.uid, ctx.isSuper)) throw err("not_found", "ไม่พบตัวละคร");
  if (!c.shareCode) {
    const code = await newShareCode();
    await fb.put(`share_codes/${code}`, c.id);
    c = await updateRow(`characters/${fk(c.id)}`, { shareCode: code });
  }
  const o = await characterFull(c, ctx.uid, ctx.isSuper);
  const [lk, sv] = await Promise.all([fb.get(`likes/${fk(ctx.uid)}/character|${fk(c.id)}`), fb.get(`likes/${fk(ctx.uid)}/save|${fk(c.id)}`)]);
  o.isLiked = !!(lk && lk.active);
  o.isSaved = !!(sv && sv.active);
  return o;
}

export async function charactersMine(p, ctx) {
  let rows = rowsOf(await fb.get("characters", { orderBy: "createdBy", equalTo: ctx.uid }));
  if (p.status) rows = rows.filter((r) => r.status === p.status);
  rows.sort((a, b) => (String(b.createdAt) < String(a.createdAt) ? -1 : 1));
  return { items: await Promise.all(rows.map((r) => characterFull(r, ctx.uid, false))) };
}

async function validTagIds(ids) {
  const valid = {};
  (await readAll("character_tags")).forEach((t) => {
    valid[t.id] = 1;
  });
  return asArray(ids, 12).filter((x) => valid[x]);
}

export async function charactersSave(p, ctx) {
  let ex = null;
  if (p.id) {
    ex = await getCharacter(str(p.id, 80));
    if (!ex) throw err("not_found", "ไม่พบตัวละคร");
    if (ex.createdBy !== ctx.uid && !ctx.isSuper) throw err("forbidden", "แก้ไขได้เฉพาะตัวละครของตัวเอง");
  }
  const draft = bool(p.draft);
  const d = {
    name: str(p.name, 40, "ชื่อตัวละคร", !draft), tagline: str(p.tagline, 100, "คำโปรย", !draft),
    bio: str(p.bio, 8000), greeting: str(p.greeting, 3000, "คำทักทาย", !draft),
    greetingImage: str(p.greetingImage, 500), image: str(p.image, 500), images: asArray(p.images, 5),
    tagIds: await validTagIds(p.tagIds), contentRating: "PG",
    flag: ["white", "green", "yellow", "red"].indexOf(p.flag) !== -1 ? p.flag : "green",
    baseLanguage: LANG_NAME[p.baseLanguage] ? p.baseLanguage : "th",
    visibility: ["public", "unlisted", "private"].indexOf(p.visibility) !== -1 ? p.visibility : "public",
  };
  const description = str(p.description, 6000, "คำอธิบาย", !draft);
  if (!draft && !d.image) throw err("bad_request", "กรุณาระบุรูปโปรไฟล์");
  if (String(p.contentRating || "").toUpperCase() === "NC") throw err("bad_request", "เว็บนี้ไม่รองรับเนื้อหา NC/18+");
  if (looksExplicit([d.name, d.tagline, description, d.bio, d.greeting].join(" "))) throw err("bad_request", "พบเนื้อหาที่ไม่เหมาะสม (ห้ามเนื้อหาทางเพศ/18+)");
  const rel = parseJson(p.initialRelationship, {}) || {};
  d.initialRelationship = { romanticValue: Math.max(0, Math.min(1000, num(rel.romanticValue))), intimacyValue: Math.max(-1000, Math.min(1000, num(rel.intimacyValue))) };
  d.personality = parseJson(p.personality, {}) || {};
  d.defaultPersonaId = p.defaultPersonaId ? str(p.defaultPersonaId, 80) : null;
  d.defaultScenarioId = p.defaultScenarioId ? str(p.defaultScenarioId, 80) : null;
  // บุคลิกการตอบแชท (7 แบบใน services/ai/persona.js) : ตั้งเองได้ ; ไม่ระบุ -> ตัวใหม่เดาจากคำอธิบาย ตัวเดิมคงค่าเดิม
  if (isPersonaId(p.persona)) d.persona = p.persona;
  else if (!ex || p.persona === "") d.persona = resolvePersona({ name: d.name, tagline: d.tagline, description, tagIds: d.tagIds });

  const needReview = (await setting("char_approval_required")) === true && !ctx.isSuper;
  const savePrompt = (id) => (description ? fb.put(`character_prompts/${fk(id)}`, { description }) : fb.remove(`character_prompts/${fk(id)}`));
  // เผยแพร่ครั้งแรก (ร่าง -> เผยแพร่ หรือสร้างใหม่แบบไม่ใช่ร่าง) ใช้ 1 สิทธิ์ ; บันทึก creditUsedAt บนตัวละคร แก้ไข/เผยแพร่ซ้ำไม่หักอีก ; super_user ไม่ใช้สิทธิ์
  const creditRequired = !draft && !ctx.isSuper && (await setting("create_credit_required")) !== false && (!ex || ex.status === "draft") && !(ex && ex.creditUsedAt);
  const chargeNote = (name) => `เผยแพร่ตัวละคร "${name}"`;
  if (ex) {
    d.status = draft ? "draft" : ex.status === "draft" || needReview ? (needReview ? "pending" : "approved") : ex.status;
    if (ex.status === "hidden" || ex.status === "rejected") d.status = needReview ? "pending" : ex.status;
    // ตัวละครที่เผยแพร่ไปแล้วก่อนมีระบบสิทธิ์ แล้วถูกย้อนกลับเป็นร่าง : ถือว่าเผยแพร่แล้ว ไม่หักตอนเผยแพร่อีกรอบ
    if (draft && ex.status !== "draft" && !ex.creditUsedAt) d.creditUsedAt = nowIso();
    let claimed = false;
    if (creditRequired) {
      // จองการหักบนตัวละคร (atomic) กันกดเผยแพร่ซ้อน/สองแท็บ : หักได้ครั้งเดียว
      await fb.transaction(`characters/${fk(ex.id)}/creditUsedAt`, (cur) => {
        claimed = false; // fn ถูกเรียกซ้ำเมื่อ ETag ชน
        if (cur) return undefined;
        claimed = true;
        return nowIso();
      });
      if (claimed) {
        try {
          await changeCredits(ctx.uid, -1, { type: "publish", refType: "character", refId: ex.id, note: chargeNote(d.name) });
        } catch (e) {
          await fb.remove(`characters/${fk(ex.id)}/creditUsedAt`).catch(() => {});
          throw e;
        }
      }
    }
    let row;
    try {
      row = await updateRow(`characters/${fk(ex.id)}`, d);
    } catch (e) {
      if (claimed) {
        await changeCredits(ctx.uid, 1, { type: "refund", refType: "character", refId: ex.id, note: "คืนสิทธิ์ เพราะบันทึกตัวละครไม่สำเร็จ" }).catch(() => {});
        await fb.remove(`characters/${fk(ex.id)}/creditUsedAt`).catch(() => {});
      }
      throw e;
    }
    await savePrompt(ex.id);
    dropCards();
    return { character: await characterFull(row, ctx.uid, ctx.isSuper) };
  }
  const mine = rowsOf(await fb.get("characters", { orderBy: "createdBy", equalTo: ctx.uid })).length;
  if (mine >= num(await setting("max_chars_per_user"), 50)) throw err("bad_request", "สร้างตัวละครครบจำนวนสูงสุดแล้ว");
  const id = newId("ch");
  const code = await newShareCode();
  Object.assign(d, {
    id, status: draft ? "draft" : needReview ? "pending" : "approved", isArchived: false, isFeatured: false, createdBy: ctx.uid,
    creatorName: ctx.user.displayName || "", shareCode: code, chatCount: 0, messageCount: 0, likeCount: 0, giftCount: 0, commentCount: 0,
  });
  if (creditRequired) {
    await changeCredits(ctx.uid, -1, { type: "publish", refType: "character", refId: id, note: chargeNote(d.name) });
    d.creditUsedAt = nowIso();
  }
  let c;
  try {
    c = await putRow(`characters/${fk(id)}`, "ch", d);
  } catch (e) {
    if (creditRequired) await changeCredits(ctx.uid, 1, { type: "refund", refType: "character", refId: id, note: "คืนสิทธิ์ เพราะบันทึกตัวละครไม่สำเร็จ" }).catch(() => {});
    throw e;
  }
  await fb.put(`share_codes/${code}`, id);
  await savePrompt(id);
  await incr(`users/${fk(ctx.uid)}`, "characterCount", 1);
  dropCards();
  return { character: await characterFull(c, ctx.uid, ctx.isSuper) };
}

export async function charactersArchive(p, ctx) {
  const c = await getCharacter(str(p.id, 80, "id", true));
  if (!c) throw err("not_found", "ไม่พบตัวละคร");
  if (c.createdBy !== ctx.uid && !ctx.isSuper) throw err("forbidden", "ไม่มีสิทธิ์");
  const row = await updateRow(`characters/${fk(c.id)}`, { isArchived: bool(p.archived) });
  dropCards();
  return { character: await characterFull(row, ctx.uid, ctx.isSuper) };
}

export async function charactersByCode(p, ctx) {
  const code = str(p.code, 20, "โค้ด", true).toUpperCase();
  rateLimit("bycode:" + ctx.uid, 30, 60);
  if (!/^[A-Z2-9]{7}$/.test(code)) throw err("bad_request", "โค้ดต้องเป็นตัวอักษร A-Z/2-9 จำนวน 7 ตัว");
  const id = await fb.get(`share_codes/${code}`);
  const c = typeof id === "string" ? await getCharacter(id) : null;
  if (!c || (c.isArchived === true && c.createdBy !== ctx.uid) || !canSeeCharacter(c, ctx.uid, ctx.isSuper)) throw err("not_found", "ไม่พบตัวละครจากโค้ดนี้");
  return { character: cardOf(c) };
}

// ---- persona / scenario ------------------------------------------------------------------------
async function listOwnOrPublic(table, p, ctx, filterFn) {
  const q = p.scope === "public" ? { orderBy: "visibility", equalTo: "public" } : { orderBy: "userId", equalTo: ctx.uid };
  let rows = rowsOf(await fb.get(table, q)).sort((a, b) => (String(b.id) < String(a.id) ? -1 : 1)).slice(0, 200);
  if (filterFn) rows = rows.filter(filterFn);
  return { items: rows };
}

export const personasList = (p, ctx) => listOwnOrPublic("personas", p, ctx, (r) => r.visibility !== "deleted");

export async function personasSave(p, ctx) {
  const d = {
    name: str(p.name, 40, "ชื่อบทบาท", true), detail: str(p.detail, 2000), image: str(p.image, 500),
    visibility: ["public", "private"].indexOf(p.visibility) !== -1 ? p.visibility : "private",
    defaultCharacterId: p.defaultCharacterId ? str(p.defaultCharacterId, 80) : null,
  };
  if (looksExplicit(d.name + " " + d.detail)) throw err("bad_request", "พบเนื้อหาที่ไม่เหมาะสม");
  if (p.id) {
    const ex = await getRow(`personas/${fk(str(p.id, 80))}`);
    if (!ex || ex.userId !== ctx.uid) throw err("not_found", "ไม่พบบทบาท");
    return { persona: await updateRow(`personas/${fk(ex.id)}`, d) };
  }
  if (rowsOf(await fb.get("personas", { orderBy: "userId", equalTo: ctx.uid })).length >= 100) throw err("bad_request", "สร้างบทบาทครบจำนวนสูงสุดแล้ว");
  return { persona: await putNew("personas", "pe", { ...d, userId: ctx.uid }) };
}

export async function personasDelete(p, ctx) {
  const ex = await getRow(`personas/${fk(str(p.id, 80, "id", true))}`);
  if (!ex || ex.userId !== ctx.uid) throw err("not_found", "ไม่พบบทบาท");
  await updateRow(`personas/${fk(ex.id)}`, { visibility: "deleted" });
  return { ok: true };
}

export const scenariosList = (p, ctx) =>
  listOwnOrPublic("scenarios", p, ctx, (r) => r.visibility !== "deleted" && (!p.characterId || !r.characterId || r.characterId === p.characterId));

export async function scenariosSave(p, ctx) {
  const d = {
    name: str(p.name, 60, "ชื่อสถานการณ์", true), detail: str(p.detail, 3000, "รายละเอียด", true),
    characterId: p.characterId ? str(p.characterId, 80) : null, visibility: ["public", "private"].indexOf(p.visibility) !== -1 ? p.visibility : "private",
  };
  if (looksExplicit(d.name + " " + d.detail)) throw err("bad_request", "พบเนื้อหาที่ไม่เหมาะสม");
  if (p.id) {
    const ex = await getRow(`scenarios/${fk(str(p.id, 80))}`);
    if (!ex || ex.userId !== ctx.uid) throw err("not_found", "ไม่พบสถานการณ์");
    return { scenario: await updateRow(`scenarios/${fk(ex.id)}`, d) };
  }
  return { scenario: await putNew("scenarios", "sc", { ...d, userId: ctx.uid, likeCount: 0 }) };
}

export async function scenariosDelete(p, ctx) {
  const ex = await getRow(`scenarios/${fk(str(p.id, 80, "id", true))}`);
  if (!ex || ex.userId !== ctx.uid) throw err("not_found", "ไม่พบสถานการณ์");
  await updateRow(`scenarios/${fk(ex.id)}`, { visibility: "deleted" });
  return { ok: true };
}

// ---- จักรวาล ------------------------------------------------------------------------------------
const universeCard = (u) => pick(u, ["id", "name", "tagline", "image", "tagIds", "characterCount", "messageCount", "chatCount", "likeCount", "createdBy", "creatorName", "createdAt"]);

export async function universesList(p, ctx) {
  let rows;
  if (bool(p.mine)) rows = rowsOf(await fb.get("universes", { orderBy: "createdBy", equalTo: ctx.uid }));
  else rows = rowsOf(await fb.get("universes", { orderBy: "status", equalTo: "approved" })).filter((u) => u.status === "approved" && u.visibility === "public");
  if (p.q) {
    const q = String(p.q).toLowerCase();
    rows = rows.filter((u) => (u.name + " " + (u.tagline || "")).toLowerCase().indexOf(q) !== -1);
  }
  rows.sort(p.sort === "popular" ? (a, b) => num(b.messageCount) - num(a.messageCount) : (a, b) => (String(b.createdAt) < String(a.createdAt) ? -1 : 1));
  const limit = limitOf(p.limit, 24), page = Math.max(1, parseInt(p.page, 10) || 1), off = (page - 1) * limit;
  return { items: rows.slice(off, off + limit).map(universeCard), page, limit, hasMore: off + limit < rows.length };
}

export async function universesGet(p, ctx) {
  const u = await getRow(`universes/${fk(str(p.id, 80, "id", true))}`);
  if (!u || !(u.createdBy === ctx.uid || ctx.isSuper || (u.status === "approved" && u.visibility !== "private"))) throw err("not_found", "ไม่พบจักรวาล");
  const o = { ...u };
  if (u.createdBy !== ctx.uid && !ctx.isSuper) delete o.description;
  const chars = (await Promise.all(asArray(u.characterIds, 20).map((id) => getCharacter(id)))).filter((c) => canSeeCharacter(c, ctx.uid, ctx.isSuper)).map(cardOf);
  return { universe: o, characters: chars };
}

export async function universesSave(p, ctx) {
  const ids = [];
  for (const id of asArray(p.characterIds, 20)) {
    if (canSeeCharacter(await getCharacter(id), ctx.uid, ctx.isSuper)) ids.push(id);
  }
  const d = {
    name: str(p.name, 40, "ชื่อจักรวาล", true), tagline: str(p.tagline, 100), history: str(p.history, 8000), description: str(p.description, 10000),
    image: str(p.image, 500), tagIds: await validTagIds(p.tagIds), characterIds: ids, characterCount: ids.length, greeting: str(p.greeting, 3000),
    memoryMode: p.memoryMode === "extended40" ? "extended40" : "standard20", baseLanguage: LANG_NAME[p.baseLanguage] ? p.baseLanguage : "th",
    visibility: ["public", "unlisted", "private"].indexOf(p.visibility) !== -1 ? p.visibility : "public",
  };
  if (looksExplicit([d.name, d.tagline, d.history, d.description, d.greeting].join(" "))) throw err("bad_request", "พบเนื้อหาที่ไม่เหมาะสม (ห้ามเนื้อหาทางเพศ/18+)");
  const needReview = (await setting("char_approval_required")) === true && !ctx.isSuper;
  if (p.id) {
    const ex = await getRow(`universes/${fk(str(p.id, 80))}`);
    if (!ex) throw err("not_found", "ไม่พบจักรวาล");
    if (ex.createdBy !== ctx.uid && !ctx.isSuper) throw err("forbidden", "แก้ไขได้เฉพาะจักรวาลของตัวเอง");
    return { universe: await updateRow(`universes/${fk(ex.id)}`, d) };
  }
  Object.assign(d, { status: needReview ? "pending" : "approved", createdBy: ctx.uid, creatorName: ctx.user.displayName || "", messageCount: 0, chatCount: 0, likeCount: 0 });
  return { universe: await putNew("universes", "un", d) };
}

// ---- ไลก์ / ติดตาม / คอมเมนต์ / โพสต์ / รายงาน / แจ้งเตือน ---------------------------------------------
const LIKE_TARGETS = { character: "characters", universe: "universes", comment: "comments", post: "posts", save: null };

export async function likeToggle(p, ctx) {
  const type = str(p.targetType, 20, "targetType", true), tid = str(p.targetId, 100, "targetId", true);
  if (!(type in LIKE_TARGETS)) throw err("bad_request", "targetType ไม่ถูกต้อง");
  const table = LIKE_TARGETS[type];
  if (table && (await fb.get(`${table}/${fk(tid)}/id`)) === null) throw err("not_found", "ไม่พบเป้าหมาย");
  const path = `likes/${fk(ctx.uid)}/${type}|${fk(tid)}`;
  let liked = true;
  await fb.transaction(path, (cur) => {
    liked = !(cur && cur.active === true);
    const now = nowIso();
    return { id: `${type}|${tid}`, userId: ctx.uid, targetType: type, targetId: tid, active: liked, createdAt: (cur && cur.createdAt) || now, updatedAt: now };
  });
  let count = null;
  if (table) {
    count = await incr(`${table}/${fk(tid)}`, "likeCount", liked ? 1 : -1);
    if (liked && type === "character") await bump("likes", tid, 1);
  }
  return { liked, likeCount: count };
}

export async function likesMine(p, ctx) {
  const type = str(p.targetType, 20, "targetType", true);
  const rows = rowsOf(await fb.get(`likes/${fk(ctx.uid)}`)).filter((l) => l.targetType === type && l.active === true);
  const r = pageRows(rows, { limit: limitOf(p.limit, 30), cursor: p.cursor, sort: "createdAt" });
  return { items: r.items.map((l) => ({ targetId: l.targetId, createdAt: l.createdAt })), nextCursor: r.nextCursor };
}

export async function followToggle(p, ctx) {
  const target = str(p.userId, 100, "userId", true);
  if (target === ctx.uid) throw err("bad_request", "ติดตามตัวเองไม่ได้");
  if (!(await getUser(target))) throw err("not_found", "ไม่พบผู้ใช้");
  const path = `follows/${fk(ctx.uid)}/${fk(target)}`;
  let on = true, existed = false;
  await fb.transaction(path, (cur) => {
    existed = !!cur;
    on = !(cur && cur.active === true);
    const now = nowIso();
    return { id: target, followerId: ctx.uid, followingId: target, active: on, createdAt: (cur && cur.createdAt) || now, updatedAt: now };
  });
  const fc = await incr(`users/${fk(target)}`, "followerCount", on ? 1 : -1);
  await incr(`users/${fk(ctx.uid)}`, "followingCount", on ? 1 : -1);
  if (on && !existed) await notify(target, "follower", "มีผู้ติดตามใหม่", ctx.user.displayName || "");
  return { following: on, followerCount: fc };
}

async function commentTargetOwner(type, id) {
  const table = { character: "characters", universe: "universes", post: "posts" }[type];
  if (!table) throw err("bad_request", "targetType ไม่ถูกต้อง");
  const t = await getRow(`${table}/${fk(id)}`);
  if (!t) throw err("not_found", "ไม่พบเป้าหมาย");
  return { table, row: t, owner: t.createdBy || t.userId };
}

export async function commentsList(p) {
  const type = str(p.targetType, 20, "targetType", true), tid = str(p.targetId, 100, "targetId", true);
  let rows = rowsOf(await fb.get("comments", { orderBy: "targetId", equalTo: tid })).filter((c) => c.targetType === type && c.isDeleted !== true);
  rows.sort((a, b) => (String(a.id) < String(b.id) ? -1 : 1));
  if (p.sort === "top") rows.sort((a, b) => (b.isPin === true) - (a.isPin === true) || num(b.likeCount) - num(a.likeCount));
  else rows.reverse();
  const off = Math.max(0, parseInt(p.cursor || 0, 10) || 0), limit = limitOf(p.limit, 30);
  return { items: rows.slice(off, off + limit), nextCursor: off + limit < rows.length ? String(off + limit) : null };
}

export async function commentAdd(p, ctx) {
  const type = str(p.targetType, 20, "targetType", true), tid = str(p.targetId, 100, "targetId", true);
  const t = await commentTargetOwner(type, tid);
  const text = str(p.text, 1000, "ข้อความ", true);
  if (looksExplicit(text)) throw err("bad_request", "ข้อความไม่เหมาะสม");
  rateLimit("cm:" + ctx.uid, 20, 60);
  if (p.parentId) {
    const par = await getRow(`comments/${fk(str(p.parentId, 80))}`);
    if (!par || par.targetId !== tid) throw err("bad_request", "ไม่พบคอมเมนต์ที่ตอบ");
  }
  const c = await putNew("comments", "cm", {
    targetType: type, targetId: tid, userId: ctx.uid, userName: ctx.user.displayName, userPhoto: ctx.user.photoURL,
    text, imageUrl: str(p.imageUrl, 500), parentId: p.parentId || null, isPin: false, isDeleted: false, likeCount: 0, creatorLiked: false,
  });
  await incr(`${t.table}/${fk(tid)}`, "commentCount", 1);
  if (t.owner && t.owner !== ctx.uid && t.owner !== "system") await notify(t.owner, "comment", "มีความคิดเห็นใหม่", text.slice(0, 80));
  return { comment: c };
}

export async function commentDelete(p, ctx) {
  const c = await getRow(`comments/${fk(str(p.id, 80, "id", true))}`);
  if (!c || c.isDeleted === true) throw err("not_found", "ไม่พบคอมเมนต์");
  const t = await commentTargetOwner(c.targetType, c.targetId);
  if (c.userId !== ctx.uid && t.owner !== ctx.uid && !ctx.isSuper) throw err("forbidden", "ไม่มีสิทธิ์ลบ");
  await updateRow(`comments/${fk(c.id)}`, { isDeleted: true });
  await incr(`${t.table}/${fk(c.targetId)}`, "commentCount", -1);
  return { ok: true };
}

export async function postsList(p, ctx) {
  const type = p.type === "short" ? "short" : "post";
  const rows = rowsOf(await fb.get("posts", { orderBy: "type", equalTo: type }));
  return pageRows(rows, {
    limit: limitOf(p.limit, 20), cursor: p.cursor,
    filter: (x) => {
      if (x.isDeleted === true) return false;
      if (x.visibility !== "public" && x.userId !== ctx.uid) return false;
      if (p.userId && x.userId !== p.userId) return false;
      if (p.characterId && asArray(x.characterIds).indexOf(p.characterId) === -1) return false;
      return true;
    },
  });
}

export async function postAdd(p, ctx) {
  const text = str(p.text, 2000), media = asArray(p.media, 9);
  if (!text && !media.length) throw err("bad_request", "กรุณาใส่ข้อความหรือรูป");
  if (looksExplicit(text)) throw err("bad_request", "ข้อความไม่เหมาะสม");
  rateLimit("post:" + ctx.uid, 10, 60);
  const post = await putNew("posts", "po", {
    userId: ctx.uid, userName: ctx.user.displayName, userPhoto: ctx.user.photoURL, type: p.type === "short" ? "short" : "post",
    text, media, characterIds: asArray(p.characterIds, 5), visibility: ["public", "private"].indexOf(p.visibility) !== -1 ? p.visibility : "public",
    likeCount: 0, commentCount: 0, isDeleted: false,
  });
  return { post };
}

export async function postDelete(p, ctx) {
  const x = await getRow(`posts/${fk(str(p.id, 80, "id", true))}`);
  if (!x || x.isDeleted === true) throw err("not_found", "ไม่พบโพสต์");
  if (x.userId !== ctx.uid && !ctx.isSuper) throw err("forbidden", "ไม่มีสิทธิ์ลบ");
  await updateRow(`posts/${fk(x.id)}`, { isDeleted: true });
  return { ok: true };
}

export async function reportCreate(p, ctx) {
  const type = str(p.targetType, 20, "targetType", true);
  if (["user", "character", "comment", "universe", "post"].indexOf(type) === -1) throw err("bad_request", "targetType ไม่ถูกต้อง");
  rateLimit("rp:" + ctx.uid, 10, 3600);
  const r = await putNew("reports", "rp", {
    reporterId: ctx.uid, targetType: type, targetId: str(p.targetId, 100, "targetId", true),
    category: str(p.category, 60, "หมวดหมู่", true), details: str(p.details, 1000), contact: str(p.contact, 100), status: "open",
  });
  return { id: r.id };
}

const unreadCount = async (uid) => (await pageByKey(`notifications/${fk(uid)}`, { limit: 50 })).items.filter((n) => n.isRead !== true).length;

export async function notificationsList(p, ctx) {
  const r = await pageByKey(`notifications/${fk(ctx.uid)}`, { limit: limitOf(p.limit, 30), cursor: p.cursor, filter: p.category ? (n) => n.category === p.category : null });
  return { items: r.items, nextCursor: r.nextCursor, unread: await unreadCount(ctx.uid) };
}

export async function notificationsRead(p, ctx) {
  const base = `notifications/${fk(ctx.uid)}`;
  if (p.id) {
    const n = await getRow(`${base}/${fk(str(p.id, 80))}`);
    if (n && n.isRead !== true) await updateRow(`${base}/${fk(n.id)}`, { isRead: true });
  } else if (bool(p.all)) {
    const list = (await pageByKey(base, { limit: 50 })).items.filter((x) => x.isRead !== true);
    await Promise.all(list.map((x) => updateRow(`${base}/${fk(x.id)}`, { isRead: true })));
  }
  return { unread: await unreadCount(ctx.uid) };
}

// ---- อัปโหลดรูป -------------------------------------------------------------------------------------
// ไม่มี Storage/Drive: เก็บรูปที่ย่อแล้วเป็น data URL ในฟิลด์รูปของข้อมูลนั้น (หน้าเว็บย่อรูปให้เล็กก่อนส่ง)
export const UPLOAD_TYPES = ["avatar", "background", "character", "persona", "post", "chat", "misc"];
const MIME_EXT = { "image/jpeg": "jpg", "image/png": "png", "image/gif": "gif", "image/webp": "webp" };
export const MAX_INLINE_IMAGE = 200 * 1024;

export async function upload(p, ctx) {
  let mime = String(p.mime || "").toLowerCase();
  if (mime.indexOf("/") === -1) mime = "image/" + mime;
  if (mime === "image/jpg") mime = "image/jpeg";
  if (!MIME_EXT[mime]) throw err("bad_request", "รองรับเฉพาะ JPG, PNG, GIF, WebP");
  const b64 = String(p.data || "").replace(/^data:[^,]*,/, "").replace(/\s+/g, "");
  if (!b64) throw err("bad_request", "ไม่มีข้อมูลรูป");
  if (Math.floor((b64.length * 3) / 4) > MAX_INLINE_IMAGE) throw err("bad_request", "รูปใหญ่เกิน " + MAX_INLINE_IMAGE / 1024 + "KB (ระบบเก็บรูปไว้ในฐานข้อมูลโดยตรง) ลองรูปที่เล็กลง");
  rateLimit("up:" + ctx.uid, 20, 3600);
  return { url: `data:${mime};base64,${b64}`, id: newId("up") };
}

// ---- ข้อกำหนด / ครีเอเตอร์ / อันดับ -----------------------------------------------------------------
export async function termsAccept(p, ctx) {
  const u = await updateRow(`users/${fk(ctx.uid)}`, { termsAcceptedAt: nowIso(), termsVersion: str(p.version, 20) || "1" });
  return { termsAcceptedAt: u.termsAcceptedAt, termsVersion: u.termsVersion };
}

async function creatorAggregate() {
  const by = {};
  (await charCards()).forEach((c) => {
    if (!c.createdBy || c.createdBy === "system") return;
    const a = by[c.createdBy] || (by[c.createdBy] = { id: c.createdBy, displayName: c.creatorName, characters: 0, chats: 0, messages: 0, likes: 0, gifts: 0 });
    a.characters++; a.chats += num(c.chatCount); a.messages += num(c.messageCount); a.likes += num(c.likeCount); a.gifts += num(c.giftCount);
  });
  return by;
}

export async function creatorsList(p, ctx) {
  const sort = ["followers", "messages", "chats", "characters", "likes"].indexOf(p.sort) !== -1 ? p.sort : "followers";
  const list = await memoize("creators:" + sort, 5 * 60 * 1000, async () => {
    const by = await creatorAggregate();
    const out = await Promise.all(Object.keys(by).map(async (id) => {
      const u = await getUser(id), a = by[id];
      a.username = u ? u.username || null : null;
      a.photoURL = u ? u.photoURL || null : null;
      a.followerCount = u ? num(u.followerCount) : 0;
      if (u) a.displayName = u.displayName || a.displayName;
      return a;
    }));
    const f = { followers: "followerCount", messages: "messages", chats: "chats", characters: "characters", likes: "likes" }[sort];
    out.sort((x, y) => num(y[f]) - num(x[f]));
    out.forEach((a, i) => {
      a.rank = i + 1;
    });
    return out.slice(0, 200);
  });
  const q = String(p.q || "").toLowerCase();
  const items = q ? list.filter((a) => (String(a.displayName) + " " + String(a.username || "")).toLowerCase().indexOf(q) !== -1) : list;
  const limit = limitOf(p.limit, 30), page = Math.max(1, parseInt(p.page, 10) || 1), off = (page - 1) * limit;
  return { items: items.slice(off, off + limit), page, limit, hasMore: off + limit < items.length, me: list.filter((a) => a.id === ctx.uid)[0] || null };
}

/** อันดับ รายวัน/สัปดาห์ จากตัวนับ /activity/<วัน>/{chats,likes,gifts}/<characterId> */
export async function rankingsOverview(p) {
  const period = p.period === "day" ? "day" : "week";
  return memoize(`rank:${period}:${dayKey()}`, 60 * 1000, async () => {
    const since = dayKey(period === "day" ? 0 : -6);
    const days = period === "day" ? [dayKey()] : [0, 1, 2, 3, 4, 5, 6].map((i) => dayKey(-i));
    const m = { chats: {}, likes: {}, gifts: {} };
    (await Promise.all(days.map((d) => fb.get(`activity/${d}`)))).forEach((a) => {
      ["chats", "likes", "gifts"].forEach((metric) => {
        Object.entries((a && a[metric]) || {}).forEach(([k, n]) => {
          const id = decodeURIComponent(k);
          m[metric][id] = (m[metric][id] || 0) + num(n);
        });
      });
    });
    const cards = {};
    (await charCards()).forEach((c) => {
      cards[c.id] = c;
    });
    const out = { period, since, characters: {}, creators: {} };
    for (const metric of ["chats", "likes", "gifts"]) {
      const ids = Object.keys(m[metric]).filter((id) => cards[id]).sort((a, b) => m[metric][b] - m[metric][a]);
      out.characters[metric] = ids.slice(0, 10).map((id, i) => ({ rank: i + 1, id, name: cards[id].name, image: cards[id].image, creatorName: cards[id].creatorName, value: m[metric][id] }));
      const cr = {};
      ids.forEach((id) => {
        const c = cards[id];
        if (c.createdBy && c.createdBy !== "system") cr[c.createdBy] = (cr[c.createdBy] || 0) + m[metric][id];
      });
      const top = Object.keys(cr).sort((a, b) => cr[b] - cr[a]).slice(0, 10);
      out.creators[metric] = await Promise.all(top.map(async (id, i) => {
        const u = await getUser(id);
        return { rank: i + 1, id, displayName: u ? u.displayName : null, photoURL: u ? u.photoURL || null : null, username: u ? u.username || null : null, value: cr[id] };
      }));
    }
    return out;
  });
}

export async function giftsRanking(p) {
  const cid = str(p.characterId, 80, "characterId", true);
  const period = ["day", "week", "month", "all"].indexOf(p.period) !== -1 ? p.period : "month";
  return memoize(`gr:${cid}:${period}:${dayKey()}`, 60 * 1000, async () => {
    const since = period === "all" ? "" : dayKey(period === "day" ? 0 : period === "week" ? -6 : -29);
    const sum = {};
    rowsOf(await fb.get("gift_tx", { orderBy: "characterId", equalTo: cid })).forEach((g) => {
      if (since && dayKey(0, new Date(g.createdAt).getTime()) < since) return;
      const a = sum[g.userId] || (sum[g.userId] = { coins: 0, count: 0 });
      a.coins += num(g.coinCost);
      a.count++;
    });
    const top = Object.keys(sum).sort((a, b) => sum[b].coins - sum[a].coins).slice(0, 10);
    const items = await Promise.all(top.map(async (id, i) => {
      const u = await getUser(id);
      return { rank: i + 1, userId: id, displayName: u ? u.displayName : null, username: u ? u.username || null : null, photoURL: u ? u.photoURL || null : null, coins: sum[id].coins, count: sum[id].count };
    }));
    return { characterId: cid, period, items };
  });
}

